import { elapsedTestAttemptMs } from '@chatsdet/types';
import { formatDurationMs } from './formatDuration';

const NOT_RECORDED = 'Not recorded';

const dateFormat = new Intl.DateTimeFormat(undefined, { month: 'short', day: 'numeric' });
const dayKeyFormat = new Intl.DateTimeFormat(undefined, {
  year: 'numeric',
  month: 'numeric',
  day: 'numeric',
});
const timeFormat = new Intl.DateTimeFormat(undefined, { hour: 'numeric', minute: '2-digit' });
const timeZoneFormat = new Intl.DateTimeFormat(undefined, {
  hour: 'numeric',
  minute: '2-digit',
  timeZoneName: 'short',
});
const dateTimeFormat = new Intl.DateTimeFormat(undefined, {
  month: 'short',
  day: 'numeric',
  hour: 'numeric',
  minute: '2-digit',
});
const dateTimeZoneFormat = new Intl.DateTimeFormat(undefined, {
  month: 'short',
  day: 'numeric',
  hour: 'numeric',
  minute: '2-digit',
  timeZoneName: 'short',
});
const preciseFormat = new Intl.DateTimeFormat(undefined, {
  year: 'numeric',
  month: 'short',
  day: 'numeric',
  hour: 'numeric',
  minute: '2-digit',
  second: '2-digit',
  timeZoneName: 'short',
});

function parseInstant(value: string | null): Date | null {
  if (!value) return null;
  const date = new Date(value);
  return Number.isFinite(date.getTime()) ? date : null;
}

/** The AM/PM marker when the locale writes it after the time ("12:25 PM"); null otherwise. */
function trailingDayPeriod(date: Date): string | null {
  const parts = timeFormat.formatToParts(date);
  const last = parts[parts.length - 1];
  return last?.type === 'dayPeriod' ? last.value : null;
}

/** Start time without its AM/PM marker, for ranges whose end time carries the same one. */
function timeWithoutDayPeriod(date: Date): string {
  return timeFormat
    .formatToParts(date)
    .filter((part) => part.type !== 'dayPeriod')
    .map((part) => part.value)
    .join('')
    .trim();
}

function precise(date: Date): string {
  return `${preciseFormat.format(date)} (${date.toISOString()})`;
}

/**
 * One compact line for a run's recorded window plus a hover title with the
 * precise start/end (seconds, UTC). `isRunning` renders an open end and the
 * elapsed time so far.
 */
export function formatRecordedWindow(
  startedAt: string | null,
  finishedAt: string | null,
  isRunning: boolean
): { label: string; title: string } {
  const start = parseInstant(startedAt);
  if (!start) return { label: NOT_RECORDED, title: NOT_RECORDED };

  const parsedEnd = parseInstant(finishedAt);
  const end = parsedEnd && parsedEnd.getTime() >= start.getTime() ? parsedEnd : null;
  const open = isRunning && !end;
  const elapsedMs = elapsedTestAttemptMs(
    start.toISOString(),
    (end ?? (open ? new Date() : null))?.toISOString()
  );
  const duration = elapsedMs === null ? null : formatDurationMs(elapsedMs);

  let window: string;
  if (open) {
    window = `${dateTimeFormat.format(start)} – Running`;
  } else if (!end) {
    window = `${dateTimeFormat.format(start)} – ${NOT_RECORDED}`;
  } else if (dayKeyFormat.format(start) === dayKeyFormat.format(end)) {
    const period = trailingDayPeriod(start);
    const startTime =
      period !== null && period === trailingDayPeriod(end)
        ? timeWithoutDayPeriod(start)
        : timeFormat.format(start);
    window = `${dateFormat.format(start)}, ${startTime} – ${timeZoneFormat.format(end)}`;
  } else {
    window = `${dateTimeFormat.format(start)} – ${dateTimeZoneFormat.format(end)}`;
  }

  const endTitle = open ? 'Running' : end ? precise(end) : NOT_RECORDED;
  return {
    label: duration ? `${window} · ${duration}` : window,
    title: `Started ${precise(start)}\nEnded ${endTitle}`,
  };
}
